/**
 * You: a settings area. Desktop = section nav + content; mobile = section list, then one section
 * per page (a back link, never a modal). Section modules live beside this file; new You content
 * goes into the module for its section.
 */
import { Link, Navigate, Outlet, useLocation } from '@tanstack/react-router'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useEffect, useSyncExternalStore } from 'react'
import { PageHead } from '../../components/ui.js'
import { YOU_SECTIONS, type YouSectionId } from './sections.js'

/** Desktop split (same breakpoint as useWide); matchMedia is missing in some test environments. */
function useWideSafe() {
  const q = '(min-width: 1024px)'
  return useSyncExternalStore(
    (cb) => {
      if (typeof matchMedia === 'undefined') return () => {}
      const m = matchMedia(q)
      m.addEventListener('change', cb)
      return () => m.removeEventListener('change', cb)
    },
    () => typeof matchMedia !== 'undefined' && matchMedia(q).matches,
    () => false,
  )
}

export function YouLayout() {
  const wide = useWideSafe()
  const { pathname, hash } = useLocation()
  const current = YOU_SECTIONS.find((s) => pathname === `/you/${s.id}`)
  useEffect(() => {
    const id = hash.replace(/^#/, '')
    if (id) queueMicrotask(() => document.getElementById(id)?.scrollIntoView?.({ block: 'start' }))
  }, [hash])

  const nav = (
    <nav aria-label="You sections" className="you-nav">
      <ul className="m-0 grid list-none gap-0.5 p-0">
        {YOU_SECTIONS.map((s) => (
          <li key={s.id}>
            <Link
              to={`/you/${s.id}` as never}
              className="you-nav__link"
              activeProps={{ 'aria-current': 'page', 'data-active': 'true' }}
            >
              <span className="grid flex-1 gap-0.5">
                <span className="text-[15px] font-medium">{s.label}</span>
                {wide ? null : <span className="text-[14px] text-text-3">{s.blurb}</span>}
              </span>
              {wide ? null : <ChevronRight size={16} className="text-text-3" aria-hidden="true" />}
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  )

  if (wide)
    return (
      <div className="app-list flex-1">
        <PageHead title="You" />
        <div className="mx-auto grid max-w-[1040px] grid-cols-[220px_minmax(0,1fr)] gap-6 px-4">
          <div className="sticky top-[72px] self-start">{nav}</div>
          <div className="min-w-0 max-w-[720px]">
            <Outlet />
          </div>
        </div>
      </div>
    )

  return (
    <div className="app-list flex-1">
      <div className="mx-auto max-w-[760px]">
        {current ? (
          <>
            <PageHead title={current.label}>
              <Link to="/you" className="vk-btn vk-btn--ghost vk-btn--sm">
                <ChevronLeft size={14} aria-hidden="true" />
                <span>You</span>
              </Link>
            </PageHead>
            <Outlet />
          </>
        ) : (
          <>
            <PageHead title="You" />
            <div className="px-2">{nav}</div>
          </>
        )}
      </div>
    </div>
  )
}

/** `/you` on its own: desktop lands on Account; mobile shows the section list (the layout's nav). */
export function YouIndex() {
  const wide = useWideSafe()
  return wide ? <Navigate to={'/you/account' as never} replace /> : null
}

export function sectionPath(id: YouSectionId) {
  return `/you/${id}` as const
}
